import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND_LIST } from "@/constants/testIds";

export default function BrandList() {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/brands");
    setBrands(data);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/brands", { name, description });
      setName("");
      setDescription("");
      setCreating(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create brand");
    }
  };

  return (
    <div data-testid={BRAND_LIST.root}>
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">Brands</p>
          <h1 className="font-serif text-5xl tracking-tight">Brands</h1>
          <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
            Each brand owns its own rules, assets, content, and approval workflow.
          </p>
        </div>
        {user?.is_platform_admin && !creating && (
          <button
            data-testid={BRAND_LIST.createButton}
            onClick={() => setCreating(true)}
            className="px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium hover:bg-[#2A2F3C]"
          >
            New brand
          </button>
        )}
      </div>

      {creating && (
        <form
          onSubmit={submit}
          className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-8 max-w-2xl"
        >
          <h3 className="font-serif text-xl mb-4">New brand</h3>
          <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
            Name
          </label>
          <input
            data-testid={BRAND_LIST.createNameInput}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] mb-4"
          />
          <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
            Description
          </label>
          <textarea
            data-testid={BRAND_LIST.createDescInput}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] mb-4"
          />
          {error && <p className="text-sm text-[#B5462F] mb-3">{error}</p>}
          <div className="flex gap-3">
            <button
              type="submit"
              data-testid={BRAND_LIST.createSubmit}
              className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
            >
              Create brand
            </button>
            <button
              type="button"
              data-testid={BRAND_LIST.createCancel}
              onClick={() => setCreating(false)}
              className="px-5 py-2 text-sm text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10 max-w-4xl">
        {brands.length === 0 && (
          <div className="px-6 py-8 text-sm text-[#6B6358]">
            No brands yet. {user?.is_platform_admin && "Create the first one to begin."}
          </div>
        )}
        {brands.map((b) => (
          <Link
            key={b.brand_id}
            to={`/brands/${b.brand_id}`}
            data-testid={`${BRAND_LIST.rowPrefix}${b.brand_id}`}
            className="block px-6 py-5 hover:bg-[#F8F5EE] transition-colors"
          >
            <div className="font-serif text-xl">{b.name}</div>
            {b.description && (
              <div className="text-sm text-[#6B6358] mt-1">{b.description}</div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
